import { isClickInContextMenu, isClickInElementWithClass } from '@lessonup/client-integration';
import { Box, newVector, PinComponent, Vector } from '@lessonup/pins-shared';
import { throttle } from 'lodash';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useEventListener } from 'usehooks-ts';
import { isBoxLayout, isComponentMoveAction, PinHoverAction, PinTransformAction } from '../../types';
import { ArrangedRailLayouts, RailLayout } from '../../types/helpLines/helpLines.types';
import { isCategoryTransformAction } from '../../types/pinComponents/pinTransformActions';
import { createRails } from '../../utils/helpLines/createRails.utils';
import { isRailEligibleAction } from '../../utils/helpLines/rails.utils';
import { isPinComponentLocked } from '../../utils/settings';
import { useUpdatePin } from '../../utils/updatePinContext/updatePinContext';
import {
  MouseEventState,
  OnMouseDownHandler,
  OnMouseEnterHandler,
  OnMouseEventHandler,
  OnMouseLeaveHandler,
} from './useHandleMouseEvents.types';
import {
  createMouseEventStateForMouseDown,
  createNewPinComponentLayout,
  createSelectionBox,
  determineSelectedPinComponents,
  isClickEventModified,
  selectionBoxIsIntersectingWithLayout,
} from './useHandleMouseEvents.utils';

export const useHandleMouseEvents = (
  pinScale: number,
  pinRendererOffset: Vector,
  pinComponents: PinComponent[],
  selectedPinComponentIds: string[],
  setModeSelecting: React.Dispatch<React.SetStateAction<string[]>>,
  setSubSelectionValue: React.Dispatch<React.SetStateAction<string | null>>,
  setSelectedPinIds: React.Dispatch<React.SetStateAction<string[]>>,
  setHighlightedPinComponentId: (componentId: string | undefined) => void,
  setModeCategoryEdit?: (categoryAreaPinComponentId: string, activeCategoryId?: string) => void
) => {
  const mouseEventState = useRef<MouseEventState | null>(null);
  const [mutatingPinComponents, setMutatingPinComponents] = useState<PinComponent[]>([]);
  const [selectionBox, setSelectionBox] = useState<Box | null>(null);
  const [railLayouts, setRailLayouts] = useState<ArrangedRailLayouts>({ horizontal: [], vertical: [] });
  const [visibleRails, setVisibleRails] = useState<RailLayout[]>([]);
  const [isMoveActionInProgress, setIsMoveActionInProgress] = useState<boolean>(false);
  const updatePinComponent = useUpdatePin();
  const releaseMutationTimeoutHandler = useRef<ReturnType<typeof setTimeout> | null>(null);
  const originalSelectionIds = useRef<string[]>([]);

  // ToDo: ED-1296 - classname is hardcoded here, this needs a better solution but that should be carefully considered
  const famClassName = 'floating-action-menu';

  const handleOnMouseDown: OnMouseDownHandler = useMemo(
    () =>
      throttle(
        (event: React.MouseEvent<HTMLDivElement>, action: PinTransformAction) => {
          const determinedSelectedPinComponentIds = determineSelectedPinComponents(action, selectedPinComponentIds);
          if (isClickInContextMenu(event) || isClickInElementWithClass(event, famClassName)) return;

          mouseEventState.current = createMouseEventStateForMouseDown(
            event,
            action,
            pinRendererOffset,
            pinScale,
            determinedSelectedPinComponentIds,
            // filter the locked components to not be included in the selection
            pinComponents.filter((pinComponent) => !isPinComponentLocked(pinComponent))
          );

          originalSelectionIds.current = selectedPinComponentIds;

          if (isCategoryTransformAction(action)) {
            setModeCategoryEdit?.(action.componentId, action.categoryId);
          } else {
            setSelectedPinIds([]);
            setModeSelecting((selectedPinComponentIds) => {
              if (isClickEventModified(event.nativeEvent)) {
                // Add onto existing selection
                const newlySelectedPinComponents = pinComponents.filter(
                  (comp) =>
                    !isPinComponentLocked(comp) &&
                    !selectedPinComponentIds.includes(comp.id) &&
                    determinedSelectedPinComponentIds.includes(comp.id)
                );

                return [...selectedPinComponentIds, ...newlySelectedPinComponents.map((comp) => comp.id)];
              } else {
                // Replace existing selection
                return determinedSelectedPinComponentIds.filter((id) => pinComponents.some((comp) => comp.id === id));
              }
            });
          }

          setSubSelectionValue(null);
          if (isRailEligibleAction(action)) {
            setRailLayouts(createRails(pinComponents, determinedSelectedPinComponentIds));
          }
        },
        10,
        { leading: true, trailing: false }
      ),
    [
      selectedPinComponentIds,
      pinRendererOffset,
      pinScale,
      pinComponents,
      setSelectedPinIds,
      setModeSelecting,
      setSubSelectionValue,
      setModeCategoryEdit,
    ]
  );

  const handleOnMouseMove: OnMouseEventHandler = useCallback(
    (event) => {
      if (!mouseEventState.current) return;
      const createdSelectionBox = createSelectionBox(event, mouseEventState.current, pinRendererOffset, pinScale);
      const mutatedPinComponents = createNewPinComponentLayout(
        event,
        mouseEventState.current,
        railLayouts,
        setVisibleRails,
        pinRendererOffset,
        pinScale
      );

      if (isComponentMoveAction(mouseEventState.current.action)) {
        setIsMoveActionInProgress(true);
      }

      // TODO: check if we can add this back in. handleOnMouseUp needs to be called after requestAnimationFrame is finished
      // requestAnimationFrame(() => {
      if (mutatedPinComponents) {
        clearMutationTimeout();
        setMutatingPinComponents(mutatedPinComponents);
      }

      if (createdSelectionBox) {
        throttledSetSelectedPinComponents(
          createdSelectionBox,
          mouseEventState.current.pinComponentsStart,
          setModeSelecting,
          originalSelectionIds.current,
          isClickEventModified(event)
        );
        setSelectionBox(createdSelectionBox);
      }
      // });
    },
    [pinRendererOffset, pinScale, railLayouts, setModeSelecting, setIsMoveActionInProgress]
  );

  const handleOnMouseUp: OnMouseEventHandler = useCallback(() => {
    setIsMoveActionInProgress(false);

    if (mutatingPinComponents.length) {
      const isCategoryAction = mouseEventState.current && isCategoryTransformAction(mouseEventState.current.action);

      if (isCategoryAction && setModeCategoryEdit) {
        const categoryAction =
          mouseEventState.current && isCategoryTransformAction(mouseEventState.current.action)
            ? mouseEventState.current.action
            : null;

        if (categoryAction) {
          requestAnimationFrame(() => {
            setModeCategoryEdit(categoryAction.componentId, categoryAction.categoryId);
          });
        }

        mutatingPinComponents.forEach((comp) => {
          if (!isPinComponentLocked(comp)) {
            updatePinComponent({
              type: 'updatePinComponentSettings',
              pinComponentId: comp.id,
              settings: comp.settings,
            });
          }
        });
      } else {
        updatePinComponent({
          type: 'updateComponentLayout',
          updates: mutatingPinComponents.reduce((acc, comp) => {
            if (isPinComponentLocked(comp)) {
              return acc;
            }

            return { ...acc, [comp.id]: comp.layout };
          }, {}),
        });
      }
    }

    setSelectionBox(null);
    mouseEventState.current = null;
    setRailLayouts({ horizontal: [], vertical: [] });
    setVisibleRails([]);

    clearMutationTimeout();
    releaseMutationTimeoutHandler.current = setTimeout(() => {
      // TODO: ED-119 the old teacher application needs to be able to apply the updates to mini mongo
      // and update the react tree. This timeout is to ensure that the updates are available
      setMutatingPinComponents([]);
    }, 200);
  }, [mutatingPinComponents, updatePinComponent, setIsMoveActionInProgress, setModeCategoryEdit]);

  const handleOnMouseEnter: OnMouseEnterHandler = useCallback(
    (event: React.MouseEvent, action: PinHoverAction) => {
      setHighlightedPinComponentId(action.componentId);
    },
    [setHighlightedPinComponentId]
  );

  const handleOnMouseLeave: OnMouseLeaveHandler = useCallback(() => {
    setHighlightedPinComponentId(undefined);
  }, [setHighlightedPinComponentId]);

  function clearMutationTimeout() {
    if (!releaseMutationTimeoutHandler.current) return;
    clearTimeout(releaseMutationTimeoutHandler.current);
  }

  useEventListener('mousemove', handleOnMouseMove);
  useEventListener('mouseup', handleOnMouseUp);

  return {
    mutatingPinComponents,
    selectionBox,
    visibleRails,
    handleOnMouseDown,
    handleOnMouseEnter,
    handleOnMouseLeave,
    isMoveActionInProgress,
  };
};

const throttledSetSelectedPinComponents = throttle(
  (
    selectionBox: Box,
    pinComponents: PinComponent[],
    setModeSelecting: React.Dispatch<React.SetStateAction<string[]>>,
    originalSelectionIds: string[],
    mergeWithOriginalSelection: boolean
  ) => {
    const selectedComponents = pinComponents
      .filter((comp) => {
        if (isPinComponentLocked(comp)) return false;
        if (mergeWithOriginalSelection && originalSelectionIds.includes(comp.id)) return true;

        return selectionBoxIsIntersectingWithLayout(selectionBox, comp.layout);
      })
      .sort(comparingPositions);

    setModeSelecting(selectedComponents.map((comp) => comp.id));
  },
  100
);

/**
 * Comparison function for `sort` that orders pin components based on their
 * positions within the pin.
 *
 * Components are ordered by their y-coordinate, with the x-coordinate used as a
 * tiebreaker.
 *
 * For line components, the comparison uses the position of their bounding box.
 */
function comparingPositions(a: PinComponent, b: PinComponent): number {
  const posA = getPosition(a);
  const posB = getPosition(b);
  return posA.y - posB.y || posA.x - posB.x;
}

/**
 * Computes the top-left position of a pin component’s bounding box.
 *
 * For box layouts, this returns the layout’s position directly. For line
 * layouts, this returns a vector of (min(x1, x2), min(y1, y2)).
 */
function getPosition(component: PinComponent): Vector {
  if (isBoxLayout(component.layout)) {
    return component.layout.position;
  }

  const { start, end } = component.layout;
  return newVector(Math.min(start.x, end.x), Math.min(start.y, end.y));
}
